import { useState } from 'react';
import { useWhatsappWebStatus, useWhatsappWebMutations, whatsappError } from '../whatsappWebApi';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ShieldAlert, QrCode, MessageCircle, FlaskConical, Lock } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { toast } from 'react-hot-toast';
import { cn } from '@/shared/utils/utils';
import { ChatSidebar } from './ChatSidebar';
import { ChatWindow } from './ChatWindow';
import { InboxSettingsDialog } from './InboxSettingsDialog';

export function WhatsappFullClient({ canManage }: { canManage: boolean }) {
  const { data: state, isLoading } = useWhatsappWebStatus();
  const { connect, disconnect } = useWhatsappWebMutations();
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (isLoading || !state) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const businessApi = state.provider === 'interakt';
  const manage = businessApi ? Boolean(state.canManage) : canManage;

  if (state.access === false) {
    return (
      <div className="flex h-full w-full items-center justify-center border-t border-outline bg-[#f0f2f5] p-6">
        <div className="max-w-sm rounded-lg bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto mb-3 h-8 w-8 text-[#54656f]" aria-hidden />
          <h2 className="mb-2 text-title-md font-semibold text-[#111b21]">No access to the WhatsApp inbox</h2>
          <p className="text-body-sm text-[#667781]">The inbox is for the team members set up as WhatsApp agents. Ask an admin if you need it.</p>
        </div>
      </div>
    );
  }

  if (state.status !== 'ready') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center border-t border-outline bg-[#f0f2f5] p-4 md:p-8">
        <div className="max-w-md rounded-lg border border-gray-100 bg-white p-6 text-center shadow-sm md:p-10">
          <h2 className="mb-4 text-display-sm font-light text-[#41525d]">WhatsApp Web Connection</h2>
          <div className="mb-8 flex gap-3 rounded-lg border-l-4 border-warning/40 bg-warning/10 p-4 text-left">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning-text" aria-hidden />
            <div className="text-body-sm text-warning-text">
              <p className="font-medium">Unofficial connection — use with care</p>
              <p className="mt-1">
                This links a normal WhatsApp number through WhatsApp Web. Meta's terms do not permit unofficial clients, and linked
                numbers can be rate-limited or banned. Connect the WhatsApp Business API (Integrations → Interakt) instead.
              </p>
            </div>
          </div>
          {state.status === 'auth_failure' && <p className="mb-2 text-body-sm font-medium text-[#41525d]">The phone ended this session — it was removed from Linked Devices, or was offline too long.</p>}
          {state.status === 'error' && <p className="mb-2 text-body-sm font-medium text-[#41525d]">Couldn't start the WhatsApp session. Try again in a moment.</p>}
          {state.lastError && <p className="mb-4 rounded-md bg-danger/10 p-3 text-left text-body-sm font-medium text-danger-text">{state.lastError}</p>}
          {state.status === 'qr' && state.qr && (
            <div className="mx-auto mb-6 flex flex-col items-center gap-4">
              <img src={state.qr} alt="WhatsApp Web QR code" className="h-64 w-64 rounded-md border border-gray-200 p-2" />
              <p className="max-w-[280px] text-body-sm text-[#54656f]">On your phone open WhatsApp → <strong>Linked devices</strong> → <strong>Link a device</strong>, then scan this code.</p>
            </div>
          )}
          {(state.status === 'starting' || state.status === 'authenticated') && (
            <div className="mb-6 flex flex-col items-center gap-3"><Spinner className="text-[#00a884]" /><p className="text-body-md font-medium text-[#54656f]">Connecting to WhatsApp…</p></div>
          )}
          {manage ? (
            <div className="flex justify-center gap-3">
              <Button
                disabled={connect.isPending || state.status === 'starting'}
                onClick={() => connect.mutate({ restart: state.status === 'qr' || state.status === 'auth_failure' || state.status === 'error' }, {
                  onError: (err) => toast.error(whatsappError(err, 'Could not start the session.')),
                })}
                size="lg"
                className="rounded-full bg-[#00a884] px-6 text-white hover:bg-[#017561]"
              >
                <QrCode className="mr-2 h-5 w-5" aria-hidden />
                {state.status === 'qr' ? 'Refresh QR code' : state.status === 'auth_failure' || state.status === 'error' ? 'Try again' : 'Link a number'}
              </Button>
              {(state.status === 'qr' || state.status === 'authenticated') && (
                <Button variant="outline" size="lg" disabled={disconnect.isPending} onClick={() => disconnect.mutate(undefined, { onSuccess: () => toast.success('Disconnected.') })} className="rounded-full px-6">
                  Cancel
                </Button>
              )}
            </div>
          ) : (
            <p className="text-body-sm text-[#54656f]">Only administrators can link or disconnect the WhatsApp number.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden border-t border-outline bg-surface">
      {businessApi && state.mode !== 'live' && (
        <div className="flex shrink-0 items-center gap-2 bg-amber-50 px-3 py-1.5 text-[12.5px] text-amber-900">
          <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            <strong>Test mode</strong> — the inbox can message only {state.testNumbers?.join(', ') || 'the test numbers'}. No client can be messaged by mistake.
          </span>
          {manage && <button type="button" onClick={() => setSettingsOpen(true)} className="shrink-0 font-medium underline">Settings</button>}
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        {/* Phones show one pane at a time: the list, or the open chat. */}
        <div className={cn('h-full w-full shrink-0 flex-col border-r border-[#d1d7db] bg-white md:flex md:w-[360px] lg:w-[400px]', selectedChat ? 'hidden' : 'flex')}>
          <ChatSidebar
            selectedChat={selectedChat}
            onSelectChat={setSelectedChat}
            me={state.me}
            seesAll={state.seesAll !== false}
            onOpenSettings={businessApi && manage ? () => setSettingsOpen(true) : undefined}
          />
        </div>
        <div className={cn('relative h-full min-w-0 flex-1 overflow-hidden bg-[#f0f2f5]', selectedChat ? 'block' : 'hidden md:block')}>
          {selectedChat ? (
            <ChatWindow key={selectedChat} chatId={selectedChat} businessApi={businessApi} onBack={() => setSelectedChat(null)} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center border-b-[6px] border-[#25d366] p-8 text-center">
              <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#25d366]/15 text-[#128c7e]">
                <MessageCircle className="h-10 w-10" aria-hidden />
              </div>
              <h3 className="mb-2 text-[26px] font-light text-[#41525d]">RBM Oil Corporation · WhatsApp</h3>
              <p className="max-w-md text-[14px] leading-[20px] text-[#667781]">
                {businessApi
                  ? 'Official WhatsApp Business inbox. Pick a conversation, or start a new chat with a client.'
                  : 'Pick a conversation on the left.'}
              </p>
            </div>
          )}
        </div>
      </div>
      {businessApi && manage && <InboxSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
